<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12">
        <b-card>
          <!-- Info Alert para novos questionários -->
          <b-alert v-if="!isEditing" variant="info" show class="mb-3">
            <i class="fa fa-info-circle"></i>
            <strong>Perguntas Pré-cadastradas:</strong>
            Este questionário foi iniciado com 7 categorias e perguntas padrão
            baseadas na NR-1. Você pode editar, remover ou adicionar novas
            categorias e perguntas conforme necessário.
          </b-alert>

          <b-form @submit.prevent="saveQuestionnaire">
            <!-- Basic Info -->
            <b-row>
              <b-col md="6">
                <b-form-group label="Título *" label-for="title">
                  <b-form-input
                    id="title"
                    v-model="form.title"
                    required
                    placeholder="Ex: Avaliação de Riscos Ocupacionais - 1º Semestre 2026"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Status" label-for="active">
                  <b-form-checkbox id="active" v-model="form.active" switch>
                    {{ form.active ? "Ativo" : "Inativo" }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group
                  label="Link de uso único (Anônimo)"
                  label-for="anonymous"
                >
                  <b-form-checkbox
                    id="anonymous"
                    v-model="form.anonymousResponses"
                    switch
                  >
                    {{ form.anonymousResponses ? "Ativado" : "Desativado" }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>

            <b-alert v-if="form.anonymousResponses" variant="warning" show>
              <i class="fa fa-exclamation-triangle"></i>
              <strong>Atenção:</strong> No modo anônimo, o acesso é feito
              exclusivamente por links individuais enviados por e-mail. O acesso
              geral via QR Code ou link público será desativado para este
              questionário.
            </b-alert>

            <b-form-group label="Descrição" label-for="description">
              <b-form-textarea
                id="description"
                v-model="form.description"
                rows="3"
                placeholder="Descreva o objetivo desta avaliação..."
              ></b-form-textarea>
            </b-form-group>

            <!-- Dates -->
            <b-row>
              <b-col md="6">
                <b-form-group label="Data de Início" label-for="startDate">
                  <b-form-input
                    id="startDate"
                    v-model="form.startDate"
                    type="date"
                  ></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Data de Término" label-for="endDate">
                  <b-form-input
                    id="endDate"
                    v-model="form.endDate"
                    type="date"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Settings -->
            <hr />
            <h5 class="mb-3">Configurações Avançadas</h5>

            <b-row>
              <b-col md="12">
                <b-form-group
                  label="Mínimo de Respostas para Analytics"
                  description="Proteção de anonimato: oculta resultados se houver menos respostas que o definido (mínimo 3)."
                >
                  <b-form-input
                    v-model="form.minResponsesForAnalytics"
                    type="number"
                    min="3"
                    max="20"
                    style="max-width: 200px"
                  ></b-form-input>
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group label="Texto de Consentimento">
              <b-form-textarea
                v-model="form.consentText"
                rows="3"
              ></b-form-textarea>
            </b-form-group>

            <b-card class="mb-4" bg-variant="light" border-variant="light">
              <h6 class="mb-2">Dados complementares na etapa 1</h6>
              <p class="text-muted small mb-3">
                Setor continua obrigatório. Ative abaixo apenas os dados
                adicionais que a empresa deseja coletar junto ao consentimento.
              </p>

              <b-row>
                <b-col
                  v-for="field in profileCollectionFields"
                  :key="field.key"
                  md="6"
                  class="mb-3"
                >
                  <div class="border rounded bg-white px-3 py-3 h-100">
                    <div
                      class="d-flex justify-content-between align-items-start"
                    >
                      <div class="pr-3">
                        <div class="font-weight-bold">{{ field.label }}</div>
                        <small class="text-muted d-block">
                          {{ field.description }}
                        </small>
                        <small v-if="field.defaultOff" class="text-muted">
                          Desativado por padrão.
                        </small>
                      </div>
                      <b-form-checkbox
                        v-model="form.profileCollection[field.key].enabled"
                        switch
                      >
                        {{
                          form.profileCollection[field.key].enabled
                            ? "Coletar"
                            : "Não coletar"
                        }}
                      </b-form-checkbox>
                    </div>

                    <b-form-checkbox
                      v-if="field.key === 'sex'"
                      v-model="form.profileCollection[field.key].diverse"
                      :disabled="!form.profileCollection[field.key].enabled"
                      switch
                      class="mt-3"
                    >
                      {{
                        form.profileCollection[field.key].diverse
                          ? "Diversidade de gênero ativada"
                          : "Diversidade de gênero desativada"
                      }}
                    </b-form-checkbox>
                    <small
                      v-if="
                        field.key === 'sex' &&
                        !form.profileCollection[field.key].enabled
                      "
                      class="text-muted d-block mt-1"
                    >
                      Ative "Coletar" para habilitar esta opção.
                    </small>
                  </div>
                </b-col>
              </b-row>
            </b-card>

            <!-- Categories -->
            <hr />
            <h5 class="mb-3">Categorias e Perguntas</h5>

            <b-alert
              v-if="isReorderingCategories && form.categories.length > 1"
              show
              variant="info"
              class="mb-3"
            >
              Arraste e solte as categorias na lista à esquerda para definir a
              nova ordem.
            </b-alert>

            <b-alert v-if="form.categories.length === 0" show variant="info">
              Adicione pelo menos uma categoria com perguntas para criar o
              questionário.
            </b-alert>

            <div v-else class="categories-layout mb-3">
              <div class="categories-sidebar">
                <div class="categories-sidebar-header">Categorias</div>
                <div class="categories-sidebar-actions">
                  <b-button
                    variant="light"
                    size="sm"
                    class="mr-2 category-icon-btn"
                    :class="{
                      'category-icon-btn-active': isReorderingCategories,
                      'category-save-btn': isReorderingCategories,
                    }"
                    @click="toggleCategoryReorderMode"
                    :disabled="form.categories.length < 2"
                    title="Editar ordem"
                  >
                    <span
                      v-if="isReorderingCategories"
                      class="category-save-text"
                      >Salvar</span
                    >
                    <i v-else class="fa fa-pencil-alt"></i>
                  </b-button>
                  <b-button
                    variant="light"
                    size="sm"
                    class="category-icon-btn"
                    @click="addCategory"
                    title="Adicionar categoria"
                  >
                    <i class="fa fa-plus"></i>
                  </b-button>
                </div>
                <div class="categories-sidebar-list">
                  <div
                    v-for="(category, catIndex) in form.categories"
                    :key="`sidebar-cat-${category.id || catIndex}`"
                    class="category-sidebar-item"
                    :class="{
                      active: activeCategoryIndex === catIndex,
                      reorderable: isReorderingCategories,
                    }"
                    :draggable="isReorderingCategories"
                    @click="setActiveCategory(catIndex)"
                    @dragstart="onCategoryDragStart(catIndex, $event)"
                    @dragover.prevent="onCategoryDragOver(catIndex, $event)"
                    @drop.prevent="onCategoryDrop(catIndex)"
                  >
                    <span class="category-sidebar-index">{{
                      catIndex + 1
                    }}</span>
                    <span class="category-sidebar-name">
                      {{ category.name || `Categoria ${catIndex + 1}` }}
                    </span>
                    <div class="category-sidebar-meta">
                      <span class="category-sidebar-count">{{
                        category.questions.length
                      }}</span>
                      <button
                        v-if="isReorderingCategories"
                        type="button"
                        class="category-delete-btn"
                        @click.stop="removeCategory(catIndex)"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="categories-content">
                <div
                  v-if="selectedCategory"
                  :key="`content-cat-${
                    selectedCategory.id || activeCategoryIndex
                  }`"
                  class="mb-4"
                >
                  <b-card class="category-card">
                    <b-row>
                      <b-col md="5">
                        <b-form-group label="Nome da Categoria *">
                          <b-form-input
                            v-model="selectedCategory.name"
                            required
                            placeholder="Ex: Ergonômico"
                          ></b-form-input>
                        </b-form-group>
                      </b-col>
                      <b-col md="7" class="text-right">
                        <b-button
                          variant="danger"
                          size="sm"
                          @click="removeCategory(activeCategoryIndex)"
                          class="mt-4"
                        >
                          <i class="fa fa-trash"></i> Remover Categoria
                        </b-button>
                      </b-col>
                    </b-row>

                    <b-form-group label="Descrição da Categoria">
                      <b-form-textarea
                        v-model="selectedCategory.description"
                        rows="2"
                        placeholder="Descrição opcional..."
                      ></b-form-textarea>
                    </b-form-group>

                    <div class="question-section-header">
                      <strong>Perguntas</strong>
                      <b-button
                        variant="success"
                        size="sm"
                        @click="addQuestion(activeCategoryIndex)"
                      >
                        <i class="fa fa-plus"></i> Adicionar Pergunta
                      </b-button>
                    </div>

                    <div
                      v-if="selectedCategory.questions.length > 0"
                      class="question-builder-list"
                    >
                      <div
                        v-for="(question, qIndex) in selectedCategory.questions"
                        :key="question.id || qIndex"
                        class="question-builder-item"
                      >
                        <div class="question-item-index">{{ qIndex + 1 }}</div>
                        <div class="question-item-input">
                          <b-form-textarea
                            v-model="question.text"
                            required
                            rows="2"
                            :placeholder="`Pergunta ${qIndex + 1}`"
                          ></b-form-textarea>
                        </div>
                        <div class="question-item-weight">
                          <b-form-select
                            v-model="question.severity"
                            :options="questionSeverityOptions"
                            required
                          ></b-form-select>
                        </div>
                        <div class="question-item-actions">
                          <b-button
                            variant="outline-danger"
                            size="sm"
                            @click="removeQuestion(activeCategoryIndex, qIndex)"
                          >
                            <i class="fa fa-trash"></i>
                          </b-button>
                        </div>
                      </div>
                    </div>

                    <b-alert v-else show variant="warning" class="mt-2 mb-0">
                      Adicione pelo menos uma pergunta para esta categoria.
                    </b-alert>
                  </b-card>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <hr />
            <b-row>
              <b-col>
                <b-button type="submit" variant="primary" :disabled="saving">
                  <b-spinner v-if="saving" small></b-spinner>
                  <i v-else class="fa fa-save"></i>
                  {{ saving ? "Salvando..." : "Salvar e Publicar" }}
                </b-button>
                <b-button
                  variant="outline-primary"
                  class="ml-2"
                  :disabled="saving"
                  @click="saveAsDraft"
                >
                  <i class="fa fa-file-alt"></i>
                  Salvar Rascunho
                </b-button>
                <b-button
                  variant="secondary"
                  class="ml-2"
                  @click="$emit('cancel')"
                >
                  Cancelar
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  getQuestionnaire,
  createQuestionnaire,
  updateQuestionnaire,
} from "@/api/nr1";

const PROFILE_COLLECTION_FIELDS = [
  {
    key: "jobFunction",
    label: "Função",
    description: "Campo de texto livre para a função exercida.",
  },
  {
    key: "ageRange",
    label: "Faixa etária",
    description: "Lista fechada de faixas etárias no preenchimento.",
  },
  {
    key: "sex",
    label: "Sexo",
    description: "Opções Masculino e Feminino.",
    defaultOff: true,
  },
  {
    key: "tenureRange",
    label: "Tempo de empresa",
    description: "Faixas pré-definidas de tempo de vínculo.",
  },
  {
    key: "workLocation",
    label: "Local de realização das atividades",
    description: "Modelo presencial, híbrido, remoto ou campo.",
  },
];

function buildDefaultProfileCollection() {
  return {
    jobFunction: { enabled: false },
    ageRange: { enabled: false },
    sex: { enabled: false, diverse: false },
    tenureRange: { enabled: false },
    workLocation: { enabled: false },
  };
}

export default {
  name: "NR1QuestionnaireForm",
  props: {
    questionnaireId: {
      type: [String, Number],
      default: null,
    },
    initialTemplateKey: {
      type: String,
      default: "risk-assessment",
    },
  },
  data() {
    return {
      saving: false,
      isEditing: false,
      activeCategoryIndex: 0,
      isReorderingCategories: false,
      draggedCategoryIndex: null,
      form: {
        title: "",
        description: "",
        active: true,
        isDraft: false,
        templateType: "risk-assessment",
        startDate: null,
        endDate: null,
        categories: [],
        anonymousResponses: true,
        minResponsesForAnalytics: 5,
        consentText:
          "Os dados coletados serão utilizados exclusivamente para fins de segurança e medicina do trabalho, conforme a NR-1. Suas respostas são anônimas e confidenciais.",
        profileCollection: buildDefaultProfileCollection(),
      },
      severityOptions: [
        { value: 1, text: "1 - Leve" },
        { value: 2, text: "2 - Moderada" },
        { value: 3, text: "3 - Grave" },
        { value: 4, text: "4 - Crítica" },
        { value: 5, text: "5 - Extrema" },
      ],
      questionSeverityOptions: [
        { value: 1, text: "Peso 1 - Leve" },
        { value: 2, text: "Peso 2 - Moderada" },
        { value: 3, text: "Peso 3 - Grave" },
        { value: 4, text: "Peso 4 - Crítica" },
        { value: 5, text: "Peso 5 - Extrema" },
      ],
    };
  },
  computed: {
    normalizedQuestionnaireId() {
      if (this.questionnaireId === null || this.questionnaireId === undefined) {
        return null;
      }

      return String(this.questionnaireId);
    },
    selectedCategory() {
      return this.form.categories[this.activeCategoryIndex] || null;
    },
    profileCollectionFields() {
      return PROFILE_COLLECTION_FIELDS;
    },
  },
  mounted() {
    if (
      this.normalizedQuestionnaireId &&
      this.normalizedQuestionnaireId !== "new"
    ) {
      this.isEditing = true;
      this.loadQuestionnaire();
    } else {
      this.form.templateType = this.initialTemplateKey || "risk-assessment";
      this.loadTemplateByKey(this.form.templateType);
      this.setActiveCategory(0);
    }
  },
  methods: {
    async loadQuestionnaire() {
      try {
        const response = await getQuestionnaire(this.normalizedQuestionnaireId);
        this.form = { ...response.data };
        this.form.isDraft = this.form.isDraft === true;
        this.form.templateType =
          this.form.templateType ||
          this.initialTemplateKey ||
          "risk-assessment";
        this.form.categories = this.form.categories || [];
        this.form.profileCollection = this.normalizeProfileCollection(
          this.form.profileCollection
        );
        this.enforceQuestionsAsRequired();
        this.normalizeQuestionSeverities();
        this.form.categories = this.form.categories.filter(
          (category) => (category.questions || []).length > 0
        );

        // Format dates
        if (this.form.startDate) {
          this.form.startDate = new Date(this.form.startDate)
            .toISOString()
            .split("T")[0];
        }
        if (this.form.endDate) {
          this.form.endDate = new Date(this.form.endDate)
            .toISOString()
            .split("T")[0];
        }

        this.setActiveCategory(0);
      } catch (error) {
        console.error("Error loading questionnaire:", error);
        this.$notify({
          type: "error",
          title: "Erro",
          text: "Erro ao carregar questionário",
        });
        this.$emit("cancel");
      }
    },
    async saveQuestionnaire() {
      await this.persistQuestionnaire(false);
    },
    async saveAsDraft() {
      await this.persistQuestionnaire(true);
    },
    async persistQuestionnaire(asDraft) {
      this.enforceQuestionsAsRequired();
      this.normalizeQuestionSeverities();
      this.form.profileCollection = this.normalizeProfileCollection(
        this.form.profileCollection
      );

      // Validate
      if (this.form.categories.length === 0) {
        this.$notify({
          type: "warning",
          title: "Atenção",
          text: "Adicione pelo menos uma categoria",
        });
        return;
      }

      for (const cat of this.form.categories) {
        if (!cat.questions || cat.questions.length === 0) {
          this.$notify({
            type: "warning",
            title: "Atenção",
            text: `A categoria "${cat.name}" precisa ter pelo menos uma pergunta`,
          });
          return;
        }

        for (const question of cat.questions) {
          const severity = Number(question.severity);
          if (!Number.isFinite(severity) || severity < 1 || severity > 5) {
            this.$notify({
              type: "warning",
              title: "Atenção",
              text: `A pergunta \"${
                question.text || "Sem texto"
              }\" precisa ter peso entre 1 e 5`,
            });
            return;
          }
        }
      }

      this.saving = true;
      try {
        const payload = {
          ...this.form,
          isDraft: asDraft,
          profileCollection: this.normalizeProfileCollection(
            this.form.profileCollection
          ),
          templateType:
            this.form.templateType ||
            this.initialTemplateKey ||
            "risk-assessment",
        };

        if (this.isEditing) {
          await updateQuestionnaire(this.normalizedQuestionnaireId, payload);
          this.$bvToast.toast(
            asDraft
              ? "Rascunho atualizado com sucesso"
              : "Questionário publicado com sucesso",
            {
              title: "Sucesso",
              toastClass: "custom-toast custom-toast-success",
              variant: "light",
              solid: true,
              autoHideDelay: 5000,
              noCloseButton: false,
            }
          );
        } else {
          await createQuestionnaire(payload);
          this.$bvToast.toast(
            asDraft
              ? "Rascunho salvo com sucesso"
              : "Questionário criado e publicado com sucesso",
            {
              title: "Sucesso",
              toastClass: "custom-toast custom-toast-success",
              variant: "light",
              solid: true,
              autoHideDelay: 5000,
              noCloseButton: false,
            }
          );
        }
        this.$emit("saved");
      } catch (error) {
        console.error("Error saving questionnaire:", error);
        this.$bvToast.toast(
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Erro ao salvar questionário",
          {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          }
        );
      } finally {
        this.saving = false;
      }
    },
    normalizeProfileCollection(profileCollection) {
      const defaults = buildDefaultProfileCollection();

      return Object.keys(defaults).reduce((result, key) => {
        const enabled =
          profileCollection &&
          profileCollection[key] &&
          profileCollection[key].enabled === true;

        if (key === "sex") {
          result[key] = {
            enabled,
            diverse:
              profileCollection &&
              profileCollection[key] &&
              profileCollection[key].diverse === true,
          };
          return result;
        }

        result[key] = { enabled };
        return result;
      }, {});
    },
    loadTemplateByKey(templateKey) {
      if (templateKey === "information-security") {
        this.loadDefaultTemplate();
        return;
      }

      this.loadDefaultTemplate();
    },
    loadDefaultTemplate() {
      // Template padrão de perguntas NR-1
      this.form.categories = [
        {
          id: `cat_${Date.now()}_1`,
          name: "Riscos Físicos",
          description: "Avaliação de exposição a agentes físicos",
          severity: 3,
          questions: [
            {
              id: `q_${Date.now()}_1_1`,
              text: "Você está exposto a níveis elevados de ruído durante sua jornada de trabalho?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_1_2`,
              text: "Você está exposto a vibração proveniente de máquinas, ferramentas ou veículos?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_1_3`,
              text: "Você trabalha em ambientes com temperaturas excessivamente altas?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_1_4`,
              text: "Você trabalha em ambientes com temperaturas excessivamente baixas?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_1_5`,
              text: "Você está exposto a radiação não ionizante (solda, UV, infravermelho, laser)?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_1_6`,
              text: "Você está exposto a radiação ionizante (raio-X, equipamentos médicos, fontes radioativas)?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_1_7`,
              text: "Você realiza atividades sob iluminação insuficiente para execução segura das tarefas?",
              required: true,
              severity: 2,
            },
            {
              id: `q_${Date.now()}_1_8`,
              text: "Você realiza atividades com iluminação excessiva ou ofuscamento?",
              required: true,
              severity: 2,
            },
            {
              id: `q_${Date.now()}_1_9`,
              text: "Você trabalha em ambientes com ventilação insuficiente?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_1_10`,
              text: "Você está exposto a umidade excessiva durante a jornada?",
              required: true,
              severity: 2,
            },
            {
              id: `q_${Date.now()}_1_11`,
              text: "Você trabalha sob pressão atmosférica anormal (câmaras hiperbáricas, mergulho, altitude)?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_1_12`,
              text: "Você está exposto a ruído de impacto (estouro, batidas metálicas, explosões)?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_1_13`,
              text: "Você permanece longos períodos exposto ao sol durante o trabalho?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_1_14`,
              text: "Você trabalha próximo a fontes de calor intenso (fornos, caldeiras, fundição)?",
              required: true,
              severity: 4,
            },
          ],
        },
        {
          id: `cat_${Date.now()}_2`,
          name: "Riscos Químicos",
          description: "Exposição a substâncias químicas",
          severity: 5,
          questions: [
            {
              id: `q_${Date.now()}_2_1`,
              text: "Você manipula produtos químicos identificados como perigosos nos rótulos ou fichas de segurança?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_2_2`,
              text: "Você tem contato direto com líquidos químicos nas atividades que executa?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_2_3`,
              text: "Você respira vapores ou gases liberados por produtos químicos?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_2_4`,
              text: "Você trabalha em local onde há poeira visível proveniente de materiais ou processos industriais?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_2_5`,
              text: "Você manipula combustíveis, solventes ou outras substâncias inflamáveis?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_2_6`,
              text: "Você manuseia ácidos, bases ou produtos com símbolo de corrosão no rótulo?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_2_7`,
              text: "Suas atividades geram fumaça, névoa ou fumos metálicos (ex.: solda ou corte)?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_2_8`,
              text: "Você executa atividades com produtos químicos em ambiente fechado?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_2_9`,
              text: "Você realiza tarefas com produtos químicos sem utilizar exaustor ou sistema de ventilação mecânica?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_2_10`,
              text: "Você manipula produtos químicos sem utilizar EPI indicado para essa atividade?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_2_11`,
              text: "Você armazena ou transporta recipientes com produtos químicos perigosos?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_2_12`,
              text: "Você mantém contato frequente da pele com produtos químicos?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_2_13`,
              text: "Você realiza atividades com produtos químicos sem consultar a Ficha de Informações de Segurança (FISPQ)?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_2_14`,
              text: "Você percebe cheiro forte ou irritação nos olhos, pele ou garganta ao utilizar produtos químicos?",
              required: true,
              severity: 3,
            },
          ],
        },
        {
          id: `cat_${Date.now()}_3`,
          name: "Riscos Ergonômicos",
          description: "Condições de trabalho e postura",
          severity: 3,
          questions: [
            {
              id: `q_${Date.now()}_3_1`,
              text: "Você realiza movimentos repetitivos com as mãos ou braços por períodos prolongados?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_3_2`,
              text: "Você permanece sentado por longos períodos sem pausa?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_3_3`,
              text: "Você permanece em pé por longos períodos sem pausa?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_3_4`,
              text: "Você levanta ou carrega peso manualmente nas suas atividades?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_3_5`,
              text: "Você transporta cargas manualmente por distâncias superiores a alguns metros?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_3_6`,
              text: "Você realiza esforço físico intenso para empurrar ou puxar objetos?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_3_7`,
              text: "Você trabalha com os braços acima da altura dos ombros?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_3_8`,
              text: "Você executa tarefas curvando o tronco para frente por períodos prolongados?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_3_9`,
              text: "Você realiza atividades ajoelhado(a) ou agachado(a) com frequência?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_3_10`,
              text: "Você utiliza ferramentas manuais que exigem força contínua das mãos?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_3_11`,
              text: "Você digita ou utiliza teclado por períodos prolongados?",
              required: true,
              severity: 2,
            },
            {
              id: `q_${Date.now()}_3_12`,
              text: "Você utiliza mouse ou dispositivo apontador por períodos prolongados?",
              required: true,
              severity: 2,
            },
            {
              id: `q_${Date.now()}_3_13`,
              text: "Você executa tarefas com ritmo acelerado imposto por máquina ou sistema?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_3_14`,
              text: "Você realiza atividades com prazos curtos que exigem aumento de ritmo de trabalho?",
              required: true,
              severity: 3,
            },
          ],
        },
        {
          id: `cat_${Date.now()}_4`,
          name: "Riscos de Acidentes",
          description: "Condições que podem causar acidentes",
          severity: 5,
          questions: [
            {
              id: `q_${Date.now()}_4_1`,
              text: "Você opera máquinas ou equipamentos com partes móveis expostas?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_4_2`,
              text: "Você trabalha próximo a máquinas em funcionamento?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_4_3`,
              text: "Você realiza atividades em altura superior a 2 metros?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_4_4`,
              text: "Você utiliza escadas portáteis nas suas atividades?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_4_5`,
              text: "Você executa tarefas próximas a bordas, aberturas ou desníveis?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_4_6`,
              text: "Você realiza atividades com risco de choque elétrico?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_4_7`,
              text: "Você executa tarefas com uso de ferramentas cortantes ou perfurantes?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_4_8`,
              text: "Você circula por áreas com piso molhado, escorregadio ou irregular?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_4_9`,
              text: "Você trabalha próximo a veículos em movimentação (empilhadeiras, caminhões, máquinas móveis)?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_4_10`,
              text: "Você executa atividades com movimentação manual de cargas suspensas?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_4_11`,
              text: "Você realiza atividades em espaços confinados?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_4_12`,
              text: "Você trabalha com risco de queda de objetos sobre sua posição?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_4_13`,
              text: "Você realiza atividades com uso de chama aberta ou fonte de ignição?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_4_14`,
              text: "Você executa tarefas isoladamente, sem outro trabalhador por perto?",
              required: true,
              severity: 4,
            },
          ],
        },
        {
          id: `cat_${Date.now()}_5`,
          name: "Equipamentos de Proteção (EPIs)",
          description: "Uso e adequação dos EPIs",
          severity: 4,
          questions: [
            {
              id: `q_${Date.now()}_5_1`,
              text: "Você realiza atividades que exigem uso de EPI?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_5_2`,
              text: "Você executa tarefas sem utilizar o EPI indicado para a atividade?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_5_3`,
              text: "Você utiliza EPI com sinais visíveis de desgaste ou dano?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_5_4`,
              text: "Você compartilha EPI de uso individual com outra pessoa?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_5_5`,
              text: "Você remove o EPI antes de finalizar a atividade que exige proteção?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_5_6`,
              text: "Você ajusta ou adapta o EPI por conta própria para conseguir trabalhar?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_5_7`,
              text: "Você realiza atividades de risco antes de receber ou substituir o EPI necessário?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_5_8`,
              text: "Você executa tarefas sem verificar se o EPI está adequado para uso?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_5_9`,
              text: "Você deixa de utilizar EPI por desconforto ou dificuldade de uso?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_5_10`,
              text: "Você realiza atividades com risco sem saber qual EPI deveria utilizar?",
              required: true,
              severity: 5,
            },
          ],
        },
        {
          id: `cat_${Date.now()}_6`,
          name: "Treinamento e Capacitação",
          description: "Conhecimento sobre segurança do trabalho",
          severity: 4,
          questions: [
            {
              id: `q_${Date.now()}_6_1`,
              text: "Você executa atividades sem ter recebido treinamento específico para essa tarefa?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_6_2`,
              text: "Você inicia uma nova atividade sem orientação prévia?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_6_3`,
              text: "Você opera máquina ou equipamento sem treinamento formal registrado?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_6_4`,
              text: "Você realiza atividades de risco sem ter participado de treinamento obrigatório?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_6_5`,
              text: "Você executa tarefas sem conhecer os procedimentos de emergência relacionados à atividade?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_6_6`,
              text: "Você realiza atividades sem saber como agir em caso de acidente?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_6_7`,
              text: "Você executa tarefas sem conhecer os riscos envolvidos na atividade?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_6_8`,
              text: "Você participa de atividades com exigência legal de capacitação sem reciclagem periódica?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_6_9`,
              text: "Você realiza atividades após mudança de função sem novo treinamento?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_6_10`,
              text: "Você executa tarefas sem receber orientação sobre uso correto de máquinas ou ferramentas?",
              required: true,
              severity: 4,
            },
          ],
        },
        {
          id: `cat_${Date.now()}_7`,
          name: "Riscos Psicossociais",
          description: "Condições psicossociais e organização do trabalho",
          severity: 3,
          questions: [
            {
              id: `q_${Date.now()}_7_1`,
              text: "Você recebe demandas com prazos que exigem acelerar o ritmo normal de trabalho?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_7_2`,
              text: "Você precisa interromper tarefas com frequência para atender novas demandas urgentes?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_7_3`,
              text: "Você executa atividades simultâneas que exigem atenção constante a múltiplas tarefas?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_7_4`,
              text: "Você realiza atividades com metas consideradas difíceis de alcançar dentro da jornada regular?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_7_5`,
              text: "Você recebe cobranças formais ou informais fora do horário de trabalho?",
              required: true,
              severity: 2,
            },
            {
              id: `q_${Date.now()}_7_6`,
              text: "Você permanece conectado a mensagens ou sistemas de trabalho fora do expediente?",
              required: true,
              severity: 2,
            },
            {
              id: `q_${Date.now()}_7_7`,
              text: "Você recebe orientações contraditórias de pessoas diferentes?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_7_8`,
              text: "Você executa tarefas sem clareza sobre suas responsabilidades?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_7_9`,
              text: "Você realiza atividades com medo de cometer erro devido a consequências disciplinares?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_7_10`,
              text: "Você evita relatar problemas por receio de reação negativa?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_7_11`,
              text: "Você presencia conflitos frequentes entre membros da equipe?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_7_12`,
              text: "Você trabalha sob exposição frequente a situações de tensão com clientes, usuários ou público externo?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_7_13`,
              text: "Você executa atividades isoladamente, sem possibilidade de apoio imediato?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_7_14`,
              text: "Você recebe tratamento desrespeitoso no contexto das atividades profissionais?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_7_15`,
              text: "Você percebe tratamento desigual entre trabalhadores em situações semelhantes?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_7_16`,
              text: "Você executa atividades com risco de exposição a eventos traumáticos (violência, acidentes graves, óbitos)?",
              required: true,
              severity: 5,
            },
          ],
        },
        {
          id: `cat_${Date.now()}_8`,
          name: "Assédio e Outras formas de Violência",
          description: "Comportamentos abusivos e violência no trabalho",
          severity: 4,
          questions: [
            {
              id: `q_${Date.now()}_8_1`,
              text: "Você é exposto(a) a gritos, xingamentos ou ofensas no ambiente de trabalho?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_8_2`,
              text: "Você recebe críticas públicas na frente de colegas ou terceiros?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_8_3`,
              text: "Você é alvo de comentários depreciativos sobre sua capacidade profissional?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_8_4`,
              text: "Você é pressionado(a) de forma repetitiva com ameaças de punição ou demissão?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_8_5`,
              text: "Você é excluído(a) deliberadamente de informações necessárias para executar seu trabalho?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_8_6`,
              text: "Você recebe tratamento diferente dos demais trabalhadores em situações semelhantes?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_8_7`,
              text: "Você é alvo de piadas ou comentários sobre características pessoais (aparência, gênero, idade, religião, origem)?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_8_8`,
              text: "Você é exposto(a) a insinuações ou comentários de natureza sexual?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_8_9`,
              text: "Você é submetido(a) a contato físico não solicitado?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_8_10`,
              text: "Você presencia comportamentos intimidatórios ou ameaçadores no ambiente de trabalho?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_8_11`,
              text: "Você evita denunciar situações inadequadas por receio de retaliação?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_8_12`,
              text: "Você já teve sua imagem profissional prejudicada por comentários intencionais de colegas ou superiores?",
              required: true,
              severity: 4,
            },
          ],
        },
        {
          id: `cat_${Date.now()}_9`,
          name: "Organização e Limpeza",
          description: "Condições gerais do ambiente",
          severity: 4,
          questions: [
            {
              id: `q_${Date.now()}_9_1`,
              text: "Você executa atividades em locais com materiais espalhados pelo piso?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_9_2`,
              text: "Você circula por áreas com cabos, fios ou mangueiras soltos no chão?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_9_3`,
              text: "Você trabalha em locais com acúmulo de resíduos ou sujeira?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_9_4`,
              text: "Você realiza atividades onde há armazenamento de materiais em locais de circulação?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_9_5`,
              text: "Você utiliza ferramentas que permanecem fora do local de guarda após o uso?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_9_6`,
              text: "Você circula por áreas com desníveis sem sinalização visível?",
              required: true,
              severity: 4,
            },
            {
              id: `q_${Date.now()}_9_7`,
              text: "Você realiza atividades próximas a empilhamento instável de materiais?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_9_8`,
              text: "Você trabalha em locais onde as saídas de emergência ficam obstruídas por objetos?",
              required: true,
              severity: 5,
            },
            {
              id: `q_${Date.now()}_9_9`,
              text: "Você executa tarefas em áreas com iluminação reduzida por acúmulo de poeira ou sujeira em luminárias?",
              required: true,
              severity: 3,
            },
            {
              id: `q_${Date.now()}_9_10`,
              text: "Você descarta resíduos em locais diferentes dos recipientes indicados?",
              required: true,
              severity: 3,
            },
          ],
        },
      ];
    },
    addCategory() {
      this.form.categories.push({
        id: `cat_${Date.now()}`,
        name: "",
        description: "",
        severity: 2,
        questions: [],
      });

      this.$nextTick(() => {
        this.setActiveCategory(this.form.categories.length - 1);
      });
    },
    removeCategory(index) {
      this.form.categories.splice(index, 1);

      if (this.form.categories.length === 0) {
        this.activeCategoryIndex = 0;
        return;
      }

      if (this.activeCategoryIndex >= this.form.categories.length) {
        this.activeCategoryIndex = this.form.categories.length - 1;
      }
    },
    addQuestion(catIndex) {
      this.form.categories[catIndex].questions.push({
        id: `q_${Date.now()}`,
        text: "",
        required: true,
        severity: 2,
      });
    },
    removeQuestion(catIndex, qIndex) {
      this.form.categories[catIndex].questions.splice(qIndex, 1);
    },
    enforceQuestionsAsRequired() {
      this.form.categories = (this.form.categories || []).map((category) => ({
        ...category,
        questions: (category.questions || []).map((question) => ({
          ...question,
          required:
            typeof question.required === "boolean" ? question.required : true,
        })),
      }));
    },
    normalizeQuestionSeverities() {
      this.form.categories = (this.form.categories || [])
        .map((category) => ({
          ...category,
          questions: (category.questions || [])
            .filter(
              (question) => (question?.responseType || "scale") !== "text"
            )
            .map((question) => {
              const fallback = Number.isFinite(Number(category.severity))
                ? Number(category.severity)
                : 2;
              const severity = Number(question.severity);

              return {
                ...question,
                severity:
                  Number.isFinite(severity) && severity >= 1 && severity <= 5
                    ? Math.round(severity)
                    : Math.max(1, Math.min(5, Math.round(fallback))),
              };
            }),
        }))
        .filter((category) => (category.questions || []).length > 0);
    },
    setActiveCategory(index) {
      if (!this.form.categories.length) {
        this.activeCategoryIndex = 0;
        return;
      }

      const safeIndex = Math.max(
        0,
        Math.min(index, this.form.categories.length - 1)
      );
      this.activeCategoryIndex = safeIndex;
    },
    toggleCategoryReorderMode() {
      if (this.form.categories.length < 2) {
        return;
      }

      this.isReorderingCategories = !this.isReorderingCategories;
      this.draggedCategoryIndex = null;
    },
    onCategoryDragStart(index, event) {
      if (!this.isReorderingCategories) {
        return;
      }

      this.draggedCategoryIndex = index;
      if (event && event.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
      }
    },
    onCategoryDragOver(index, event) {
      if (!this.isReorderingCategories || this.draggedCategoryIndex === null) {
        return;
      }

      if (event && event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }

      if (index === this.draggedCategoryIndex) {
        return;
      }

      const categories = [...this.form.categories];
      const [draggedCategory] = categories.splice(this.draggedCategoryIndex, 1);
      categories.splice(index, 0, draggedCategory);
      this.form.categories = categories;
      this.draggedCategoryIndex = index;
      this.activeCategoryIndex = index;
    },
    onCategoryDrop(index) {
      if (!this.isReorderingCategories) {
        return;
      }

      this.draggedCategoryIndex = null;
      this.activeCategoryIndex = index;
    },
  },
};
</script>

<style scoped>
.card {
  margin-bottom: 1rem;
}

.categories-layout {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 1rem;
}

.categories-sidebar {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 0.75rem;
  align-self: start;
  position: sticky;
  top: 0.5rem;
}

.categories-sidebar-header {
  font-size: 0.9rem;
  font-weight: 700;
  color: #4a5568;
  margin-bottom: 0.65rem;
  padding-right: 7.5rem;
}

.categories-sidebar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  position: absolute;
  top: 0.55rem;
  right: 0.6rem;
}

.category-icon-btn {
  width: 25px;
  height: 25px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid #cbd5e0;
  background: #ffffff;
  color: #111827;
  box-shadow: none;
}

.category-icon-btn i {
  font-size: 0.85rem;
}

.category-icon-btn-active {
  background: #111827;
  color: #ffffff;
  border-color: #111827;
}

.category-save-btn {
  width: auto;
  min-width: 58px;
  padding: 0 0.55rem;
}

.category-save-text {
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1;
}

.categories-sidebar-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 58vh;
  overflow-y: auto;
}

.category-sidebar-item {
  width: 100%;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  background: #ffffff;
  color: #2d3748;
  padding: 0.55rem 0.6rem;
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
  cursor: pointer;
}

.category-sidebar-item.active {
  border-color: #90cdf4;
  background: #ebf8ff;
  box-shadow: inset 3px 0 0 #3182ce;
}

.category-sidebar-item.reorderable {
  cursor: move;
}

.category-sidebar-index {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #edf2f7;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
}

.category-sidebar-item.active .category-sidebar-index {
  background: #bee3f8;
  color: #1a365d;
}

.category-sidebar-name {
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.category-sidebar-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #718096;
}

.category-sidebar-meta {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.category-delete-btn {
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff5f5;
  color: #e53e3e;
}

.category-delete-btn:hover {
  background: #fed7d7;
}

.categories-content {
  min-width: 0;
}

.category-card {
  border-width: 1px;
}

.question-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.question-builder-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.question-builder-item {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 180px auto;
  gap: 0.6rem;
  align-items: start;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  padding: 0.65rem;
}

.question-item-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #2d3748;
  font-weight: 700;
  font-size: 0.78rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.15rem;
}

.question-item-input {
  min-width: 0;
}

.question-item-weight {
  min-width: 150px;
}

.question-item-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

@media (max-width: 991px) {
  .categories-layout {
    grid-template-columns: 1fr;
  }

  .categories-sidebar {
    position: static;
  }
}
</style>
